import React, { useCallback, useMemo } from 'react';
import { StyleSheet, ScrollView } from 'react-native';
import { DNABox, DNAText, DNAButton, DNACheckbox } from '@alucio/lux-ui';
import { DNAChip } from 'src/components/DNA';
import {
  usePresentationSelector,
} from 'src/components/PresentationBuilder/PresentationSelector/PresentationSelectorStateProvider';
import { usePresentationBuilderState } from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import { useCloneBuilder, POOL_CONTAINER_ID } from 'src/components/DnD/Clone/CloneBuilder';
import { SortableItem, SortableItemProps } from 'src/components/DnD/DnDWrapper';
import { useCurrentUser } from 'src/state/redux/selector/user';
import { DocumentAccessLevel, Page } from '@alucio/aws-beacon-amplify/src/models';
import {
  CollapsibleGroupCard,
  GroupDraft,
  SlideThumb,
} from 'src/components/SlideSelector/PageGroupList';
import { thumbnailSizeDimensions } from 'src/hooks/useThumbnailSize/useThumbnailSize';
import CollapsibleCard, { CollapsibleActionMenu } from 'src/components/CollapsibleCard/CollapsibleCard';
import { sortGroups } from 'src/utils/groupHelpers';

const styles = StyleSheet.create({
  allSlides: {
    height: 48,
    borderRadius: 4,
  },
  overlay: {
    display: 'none',
    marginBottom: 24,
  },
  overlayDragging: {
    opacity: 0.3,
    display: 'flex',
  },
});

const OverlayGroupCard: React.FC<{activeItem: GroupDraft}> = ({ activeItem }) => {
  const { selectedDocumentVersionORM } = usePresentationSelector()

  const groupHeaderTitle = (
    <DNABox fill appearance="col" alignY="center" style={{ width: 100 }}>
      <DNAText h5 numberOfLines={1}>{activeItem.name}</DNAText>
      <DNAText c1 status="tertiary" numberOfLines={1}>{selectedDocumentVersionORM?.model.title}</DNAText>
    </DNABox>
  )
  return (
    <DNABox style={{ height: 71 }} fill>
      <CollapsibleCard
        headerTitle={groupHeaderTitle}
        isCollapsed={true}
        selected={true}
      >
        <CollapsibleActionMenu>
          <DNABox
            spacing="sm"
            alignY="center"
          >
            <DNAChip.Beacon children={`${activeItem.pages.length}`} />
            {/* These buttons are just placeholder, they are not functional */}
            <DNAButton
              appearance="ghost"
              status="subtle"
              iconLeft={activeItem.locked ? 'lock' : 'lock-simple-open'}
              disabled
            />
            <DNACheckbox
              checked={true}
              status="gray"
            />
          </DNABox>
        </CollapsibleActionMenu>
      </CollapsibleCard>
    </DNABox>
  )
}

const SlideGroupPool : React.FC<{
  handleSelectGroup: (group: GroupDraft) => void,
  handleSelectSlide: (slide: Page) => void,
  index: number,
  shouldExpand: boolean,
}> = (props) => {
  const {
    index,
    handleSelectGroup,
    handleSelectSlide,
    shouldExpand,
  } = props

  const {
    selectedDocumentVersionORM,
    selectorThumbnailSize,
    selectedItemIds,
    contentPageData,
    deckSearchText,
  } = usePresentationSelector()

  const { collapsibleGroups, poolItemsGroups } = SlideGroupHook()
  const { editorThumbnailSize } = usePresentationBuilderState()
  const { activeId, activeContainerOrigin } = useCloneBuilder()

  const poolItem = useMemo(() => poolItemsGroups[index], [poolItemsGroups, index])
  const { id, itemId } = poolItem

  const cardContainerWidth = useMemo(() => thumbnailSizeDimensions[editorThumbnailSize].width + 52,
    [editorThumbnailSize])

  const sortableHeader : SortableItemProps = useMemo(() => ({
    id:id,
    itemId:itemId,
    containerId:POOL_CONTAINER_ID,
    style: StyleSheet.flatten([(activeId && activeContainerOrigin.current === POOL_CONTAINER_ID &&
      selectedItemIds.includes(itemId))
      ? { width: cardContainerWidth, position: 'absolute', visibility: 'hidden' }
      : undefined,
    ]),
  }), [activeId, activeContainerOrigin, cardContainerWidth, id, itemId, selectedItemIds])

  const containerStyle = useMemo(() => StyleSheet.flatten([
    styles.overlay,
    (activeId && activeContainerOrigin.current === POOL_CONTAINER_ID &&
    selectedItemIds.includes(itemId))
      ? styles.overlayDragging
      : undefined,
  ]), [activeContainerOrigin, activeId, itemId, selectedItemIds])

  const onCheck = useCallback(() => {
    handleSelectGroup(collapsibleGroups[index])
  }, [collapsibleGroups, handleSelectGroup, index])

  return (
    <DNABox appearance="col" key={`${collapsibleGroups[index].id}-container`}>
      {/* When dragging start, to enable the group overlay's center to be snapped on cursor and for better UX,
    the actual group card will have to transform size to match the overlay.
    But we do not want to show that at slide pool, so OverlayGroupCard is a fake group card
    that is laying on top of the invisible actual groupcard while dragging. */}
      <DNABox
        key={`${collapsibleGroups[index].id}-overlay`}
        fill
        style={containerStyle}
      >
        <OverlayGroupCard activeItem={collapsibleGroups[index]}/>
      </DNABox>
      <DNABox
        key={collapsibleGroups[index].id}
        fill
        appearance="col"
        style={{ marginBottom: 24 }}
      >
        <CollapsibleGroupCard
          key={collapsibleGroups[index].id}
          group={collapsibleGroups[index]}
          documentVersionORM={selectedDocumentVersionORM}
          onCheck={onCheck}
          selectorThumbnailSize={selectorThumbnailSize}
          contentPageData={contentPageData}
          sortableHeader={sortableHeader}
          handleSelectSlide={handleSelectSlide}
          isCollapsed={!shouldExpand}
          deckSearchText={deckSearchText}
        />
      </DNABox>
    </DNABox>
  )
}

const SlideGroupHook = () => {
  const {
    selectedDocumentVersionORM,
    selectedGroups,
    handleSelectGroup,
    getAllGroups,
    getAllGroupedIndividualSlides,
    deckSearchText,
    contentPageData,
  } = usePresentationSelector()
  const { poolItems } = useCloneBuilder()
  const currentUser = useCurrentUser()

  const collapsibleGroups = useMemo(() => {
    return getAllGroups().sort(sortGroups())
  }, [getAllGroups])

  const matchesSearch = useCallback((group: GroupDraft): boolean => {
    if (!deckSearchText || deckSearchText.trim().length === 0) return true

    const normalizedSearch = deckSearchText.toLowerCase()
    return group.pages.some(p => {
      const title = contentPageData?.[p.number - 1]?.title || ''
      const content = contentPageData?.[p.number - 1]?.content || ''
      return title.toLowerCase().includes(normalizedSearch) ||
             content.toLowerCase().includes(normalizedSearch)
    })
  }, [deckSearchText, contentPageData])

  const visibleCollapsibleGroups = useMemo(() =>
    collapsibleGroups.filter(matchesSearch),
  [collapsibleGroups, matchesSearch],
  )

  const allIndividualSlideGroups = getAllGroupedIndividualSlides()

  const visibleIndividualSlideGroups = useMemo(() =>
    allIndividualSlideGroups.filter(matchesSearch),
  [allIndividualSlideGroups, matchesSearch],
  )

  const poolItemsGroups = useMemo(() => {
    return collapsibleGroups
      .map((group, index) => visibleCollapsibleGroups.includes(group) ? poolItems[index] : null)
      .filter((item): item is { id: string; itemId: string } => !!item)
  }, [poolItems, collapsibleGroups, visibleCollapsibleGroups])

  const poolItemsIndividualSlides = useMemo(() => {
    const startIndex = collapsibleGroups.length
    return allIndividualSlideGroups
      .map((group, index) => visibleIndividualSlideGroups.includes(group) ? poolItems[startIndex + index] : null)
      .filter((item): item is { id: string; itemId: string } => !!item)
  }, [poolItems, collapsibleGroups.length, allIndividualSlideGroups, visibleIndividualSlideGroups])

  const handleSelectSlide = useCallback((page: Page) => {
    const group = allIndividualSlideGroups
      .find(group => group.pages.find(pageWithGroup => pageWithGroup.pageId === page.pageId))

    if (!group) return

    handleSelectGroup(group)
  }, [allIndividualSlideGroups, handleSelectGroup])

  const modifiable = useMemo(
    () => selectedDocumentVersionORM?.meta.permissions.MSLSelectSlides,
    [selectedDocumentVersionORM],
  )

  const documentORMModel = selectedDocumentVersionORM?.relations.documentORM.model
  const isUserUploads = documentORMModel?.accessLevel === DocumentAccessLevel.USER &&
    documentORMModel.createdBy === currentUser.authProfile?.attributes.email

  const handleForcedSelectedCheck = (group: GroupDraft) => {
    let isForcedSelected: boolean = group.pages.some(page => page.isRequired)
    // Check if the selectedGroups already has the required slide(s)
    if (isForcedSelected) {
      selectedGroups?.forEach(selectedGroup => {
        selectedGroup.pages.forEach(slide => {
          const requiredPage = group.pages.find(page => page.isRequired)
          // If the selectedGroups has the required slide already, then no need to force select
          if (slide.pageId === requiredPage?.pageId) isForcedSelected = false
        })
      })
    }
    // Check if the current selected/checked group has the required slides
    if (isForcedSelected) {
      collapsibleGroups.forEach(collapsibleGroup => {
        if (collapsibleGroup.checked) {
          collapsibleGroup.pages.forEach(slide => {
            const requiredPage = group.pages.find(page => page.isRequired)
            if (slide.pageId === requiredPage?.pageId) {
              isForcedSelected = false
              requiredPage.checked = false
            }
          })
        }
      })
    }
    // If the selectGroups does not hve required slide, then modified the slide to be checked
    if (isForcedSelected) {
      group.pages.forEach(page => {
        page.checked = true
      })
    }
  }

  return {
    collapsibleGroups: visibleCollapsibleGroups,
    poolItemsGroups,
    poolItemsIndividualSlides,
    handleSelectSlide,
    modifiable,
    handleForcedSelectedCheck,
    isUserUploads,
    allIndividualSlideGroups: visibleIndividualSlideGroups,
    visibleIndividualSlideGroups,
    matchesSearch,
  }
}

const SlidesGroupPool: React.FC = () => {
  const {
    selectedDocumentVersionORM,
    selectorThumbnailSize,
    handleSelectGroup,
    selectedItemIds,
    contentPageData,
    deckSearchText,
  } = usePresentationSelector()
  const { activeId, activeContainerOrigin } = useCloneBuilder()
  const {
    collapsibleGroups,
    poolItemsGroups,
    poolItemsIndividualSlides,
    handleSelectSlide,
    modifiable,
    handleForcedSelectedCheck,
    isUserUploads,
    allIndividualSlideGroups,
    visibleIndividualSlideGroups,
    matchesSearch,
  } = SlideGroupHook()

  const filteredPreviewPages = visibleIndividualSlideGroups.map((group) => group.pages[0])

  const groups = useMemo(() => poolItemsGroups.map(({ itemId }, index) => {
    const hasSearchText = !!(deckSearchText && deckSearchText.trim().length > 0)
    const shouldExpand = hasSearchText && matchesSearch(collapsibleGroups[index])
    return (
      <SlideGroupPool
        key={`${itemId}`}
        index={index}
        handleSelectGroup={handleSelectGroup}
        handleSelectSlide={handleSelectSlide}
        shouldExpand={shouldExpand}
      />
    )
  }), [poolItemsGroups, handleSelectGroup, handleSelectSlide, deckSearchText, matchesSearch, collapsibleGroups])

  if (!selectedDocumentVersionORM) return null

  return (
    <DNABox
      testID="slide-pool-container"
      fill
      style={{ marginHorizontal: 64 }}
    >
      <DNABox as={ScrollView} appearance="col">
        {/* All the groups */}
        {!!collapsibleGroups.length &&
          <DNABox appearance="col">

            {/* Header */}
            <DNABox alignX="center" alignY="center" style={styles.allSlides}>
              <DNAText h5>Groups</DNAText>
            </DNABox>

            {/* Groups */}
            {groups}
          </DNABox>}

        {/* All individual slides */}
        {(modifiable || isUserUploads) &&
          <DNABox appearance="col">

            {/* Header */}

            <CollapsibleCard
              contentPadding="none"
              headerTitle={
                <DNABox alignX="center" alignY="center" style={styles.allSlides} fill>
                  <DNAText h5>View all slides</DNAText>
                </DNABox>
          }
              isCollapsed={false}
            >

              {/* Slides */}
              <DNABox wrap="start" alignX="center" style={{ paddingVertical: 18 }}>
                {
                  poolItemsIndividualSlides.map(({ id, itemId }, index) => {
                    if (!visibleIndividualSlideGroups[index]) return null
                    handleForcedSelectedCheck(visibleIndividualSlideGroups[index])
                    const page = visibleIndividualSlideGroups[index].pages[0]
                    const title = contentPageData?.find(pageData => pageData.pageId === page.pageId)?.title ||
                      contentPageData?.find(pageData => pageData.documentPageNumber === page.number)?.title ||
                      contentPageData?.[page.number - 1]?.title || ''

                    return (
                      <SortableItem
                        key={itemId}
                        id={id}
                        itemId={itemId}
                        containerId={POOL_CONTAINER_ID}
                        style={
                          StyleSheet.flatten([
                            (activeId && activeContainerOrigin.current === POOL_CONTAINER_ID &&
                              selectedItemIds.includes(itemId))
                              ? { opacity: 0.3 }
                              : undefined,
                          ])
                        }
                      >
                        <DNABox appearance="col" style={{ marginHorizontal: 6 }}>
                          <SlideThumb
                            page={page}
                            documentVersionORM={selectedDocumentVersionORM}
                            previewPages={filteredPreviewPages}
                            onCheck={!page.isPlaceholder
                              ? () => { handleSelectGroup(allIndividualSlideGroups[index]) }
                              : undefined}
                            showAssociation={page.checked}
                            selectorThumbnailSize={selectorThumbnailSize}
                            contentPageData={contentPageData}
                            title={title}
                          />
                        </DNABox>
                      </SortableItem>
                    )
                  })
                }
              </DNABox>
            </CollapsibleCard>
          </DNABox>
        }

      </DNABox>
    </DNABox>
  )
}

export default SlidesGroupPool
